import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  SmartCheckbox,
  SmartDateTimePicker,
  SmartForm,
  useSmartForm,
} from "@nodeon/react-components";
import { useSnackbar } from "notistack";
import React from "react";
import { useTranslation } from "react-i18next";
import { useFileShare } from "./useFileShare";

type Props = {
  open: boolean;
  onClose: () => void;
  nodeId: number;
};

interface FormInputs {
  expires: Date | null;
  singleUse: boolean;
}

export const FileShareDialog = ({
  open,
  onClose,
  nodeId,
}: Props): JSX.Element => {
  const { t } = useTranslation("file-explorer");
  const { share, getShareLinkFromCode } = useFileShare();
  const [shareLink, setShareLink] = React.useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const defaultValues = React.useMemo<FormInputs>(
    () => ({ singleUse: false, expires: null }),
    [],
  );
  const formMethods = useSmartForm<FormInputs>({ defaultValues });

  const handleSubmit = React.useCallback(
    (formState: FormInputs) => {
      return share.mutateAsync({
        dto: {
          singleUse: formState.singleUse,
          expires: formState.expires ?? undefined,
        },
        nodeId,
      });
    },
    [nodeId, share],
  );

  const handleSuccess = React.useCallback(
    (_d: unknown, code: string) => {
      const link = getShareLinkFromCode(code);
      setShareLink(link);
    },
    [getShareLinkFromCode],
  );

  const handleCopyClick = () => {
    if (!shareLink) return;
    try {
      if ("clipboard" in navigator) {
        navigator.clipboard.writeText(shareLink).catch(() => {
          throw new Error("");
        });
      } else {
        document.execCommand("copy", true, shareLink);
      }
      enqueueSnackbar(t("link-copy-success"), { variant: "info" });
    } catch {
      enqueueSnackbar(t("link-copy-failed"), { variant: "warning" });
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{t("share-dialog-title")}</DialogTitle>
      <DialogContent>
        <SmartForm<FormInputs, string>
          methods={formMethods}
          onSubmit={handleSubmit}
          onSuccess={handleSuccess}
        >
          <SmartDateTimePicker<FormInputs>
            name="expires"
            label={t("expiration")}
            textfieldProps={{ fullWidth: true }}
            gridProps={{ xs: 10 }}
          />
          <SmartCheckbox<FormInputs>
            name="singleUse"
            label={t("single-use")}
            gridProps={{ xs: 10 }}
          />
          <Button variant="contained" color="secondary" type="submit">
            {t("create-link")}
          </Button>
        </SmartForm>
        {shareLink !== null ? (
          <Box
            sx={{
              display: "grid",
              alignItems: "end",
              gridTemplateColumns: "1fr auto",
              columnGap: 2,
            }}
          >
            <TextField
              value={shareLink}
              label={t("share-link")}
              fullWidth
              sx={{ mt: 4 }}
            />
            <Box>
              <Tooltip title={String(t("copy-to-clipboard"))}>
                <IconButton onClick={handleCopyClick}>
                  <ContentCopyOutlinedIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
